import React, { useMemo, useState } from 'react';
import type { PmFilterOperator, PmFilterState, PmFilterRule } from '../lib/pmFilters';
import { normalizePmFilterState } from '../lib/pmFilters';

export interface PmFilterFieldDef {
  field: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'select' | 'boolean';
  options?: Array<{ value: string; label: string }>;
}

interface PmFilterBuilderProps {
  title: string;
  placeholder: string;
  value: PmFilterState;
  onChange: (next: PmFilterState) => void;
  availableFields: PmFilterFieldDef[];
}

const PmFilterBuilder: React.FC<PmFilterBuilderProps> = ({
  title,
  placeholder,
  value,
  onChange,
  availableFields,
}) => {
  const [expanded, setExpanded] = useState(false);

  const state = useMemo(() => normalizePmFilterState(value), [value]);

  const operatorsByType: Record<string, Array<{ value: PmFilterOperator; label: string }>> = {
    text: [
      { value: 'contains', label: 'Contains' },
      { value: 'equals', label: 'Equals' },
      { value: 'starts_with', label: 'Starts with' },
      { value: 'ends_with', label: 'Ends with' },
    ],
    number: [
      { value: 'equals', label: 'Equals' },
      { value: 'greater_than', label: 'Greater than' },
      { value: 'less_than', label: 'Less than' },
      { value: 'between', label: 'Between' },
    ],
    date: [
      { value: 'equals', label: 'On' },
      { value: 'greater_than', label: 'After' },
      { value: 'less_than', label: 'Before' },
      { value: 'between', label: 'Between' },
    ],
    select: [
      { value: 'equals', label: 'Is' },
      { value: 'in', label: 'Is any of' },
      { value: 'not_in', label: 'Is none of' },
    ],
    boolean: [{ value: 'equals', label: 'Is' }],
  };

  const getFieldDef = (field: string) => availableFields.find(f => f.field === field) || null;

  const update = (next: Partial<PmFilterState>) => {
    onChange({
      ...state,
      ...next,
      rules: next.rules ? next.rules : state.rules,
    });
  };

  const addRule = () => {
    update({ rules: [...state.rules, { field: '', operator: 'contains', value: '' }] });
  };

  const updateRule = (index: number, patch: Partial<PmFilterRule>) => {
    const rules = [...state.rules];
    rules[index] = { ...rules[index], ...patch } as PmFilterRule;
    update({ rules });
  };

  const removeRule = (index: number) => {
    update({ rules: state.rules.filter((_, i) => i !== index) });
  };

  const clearAll = () => {
    onChange({ query: '', combinator: 'and', rules: [] });
  };

  const renderValue = (rule: PmFilterRule, idx: number) => {
    const def = getFieldDef(rule.field);
    const type = def?.type || 'text';
    const options = def?.options || [];

    if (type === 'select' && options.length > 0) {
      if (rule.operator === 'in' || rule.operator === 'not_in') {
        const current = Array.isArray(rule.value) ? rule.value : [];
        return (
          <select
            multiple
            value={current}
            onChange={(e) => {
              const values = Array.from(e.target.selectedOptions, o => o.value);
              updateRule(idx, { value: values });
            }}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            {options.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        );
      }

      return (
        <select
          value={rule.value || ''}
          onChange={(e) => updateRule(idx, { value: e.target.value })}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="">Select...</option>
          {options.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      );
    }

    if (type === 'boolean') {
      return (
        <select
          value={rule.value === true ? 'true' : rule.value === false ? 'false' : ''}
          onChange={(e) => {
            const v = e.target.value;
            if (!v) updateRule(idx, { value: '' });
            else updateRule(idx, { value: v === 'true' });
          }}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="">Select...</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      );
    }

    if (type === 'date') {
      if (rule.operator === 'between') {
        const v0 = Array.isArray(rule.value) ? rule.value[0] : '';
        const v1 = Array.isArray(rule.value) ? rule.value[1] : '';
        return (
          <div className="flex gap-2">
            <input
              type="date"
              value={v0 || ''}
              onChange={(e) => updateRule(idx, { value: [e.target.value, v1 || ''] })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <input
              type="date"
              value={v1 || ''}
              onChange={(e) => updateRule(idx, { value: [v0 || '', e.target.value] })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
        );
      }

      return (
        <input
          type="date"
          value={rule.value || ''}
          onChange={(e) => updateRule(idx, { value: e.target.value })}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
        />
      );
    }

    if (type === 'number') {
      if (rule.operator === 'between') {
        const v0 = Array.isArray(rule.value) ? rule.value[0] : '';
        const v1 = Array.isArray(rule.value) ? rule.value[1] : '';
        return (
          <div className="flex gap-2">
            <input
              type="number"
              value={v0 || ''}
              onChange={(e) => updateRule(idx, { value: [e.target.value, v1 || ''] })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <input
              type="number"
              value={v1 || ''}
              onChange={(e) => updateRule(idx, { value: [v0 || '', e.target.value] })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
        );
      }

      return (
        <input
          type="number"
          value={rule.value || ''}
          onChange={(e) => updateRule(idx, { value: e.target.value })}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
        />
      );
    }

    return (
      <input
        type="text"
        value={rule.value || ''}
        onChange={(e) => updateRule(idx, { value: e.target.value })}
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
      />
    );
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <div className="flex items-center gap-3 border-b border-gray-100 p-4">
        <div className="flex-1">
          <div className="text-xs font-semibold text-gray-600">{title}</div>
          <input
            type="text"
            value={state.query}
            onChange={(e) => update({ query: e.target.value })}
            placeholder={placeholder}
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </div>

        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          {expanded ? 'Hide' : 'Show'} Filters
          {state.rules.length > 0 ? ` (${state.rules.length})` : ''}
        </button>

        <button
          type="button"
          onClick={clearAll}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Clear
        </button>
      </div>

      {expanded && (
        <div className="space-y-4 bg-gray-50 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="text-sm font-semibold text-gray-900">Rules</div>
              <select
                value={state.combinator}
                onChange={(e) => update({ combinator: e.target.value === 'or' ? 'or' : 'and' })}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="and">Match all</option>
                <option value="or">Match any</option>
              </select>
            </div>
            <button
              type="button"
              onClick={addRule}
              className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              Add Rule
            </button>
          </div>

          {state.rules.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-gray-500">
              No filters yet. Click "Add Rule".
            </div>
          ) : (
            <div className="space-y-3">
              {state.rules.map((rule, idx) => {
                const def = getFieldDef(rule.field);
                const type = def?.type || 'text';
                const ops = operatorsByType[type] || operatorsByType.text;
                return (
                  <div key={idx} className="grid grid-cols-12 gap-2 rounded-lg bg-white p-3">
                    <div className="col-span-4">
                      <select
                        value={rule.field}
                        onChange={(e) => {
                          const field = e.target.value;
                          const fdef = getFieldDef(field);
                          const ftype = fdef?.type || 'text';
                          const defaultOp = (operatorsByType[ftype] || operatorsByType.text)[0]?.value || 'contains';
                          updateRule(idx, { field, operator: defaultOp, value: '' });
                        }}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      >
                        <option value="">Select field...</option>
                        {availableFields.map(f => (
                          <option key={f.field} value={f.field}>{f.label}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-span-3">
                      <select
                        value={rule.operator}
                        onChange={(e) => updateRule(idx, { operator: e.target.value as PmFilterOperator, value: '' })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                        disabled={!rule.field}
                      >
                        {ops.map(op => (
                          <option key={op.value} value={op.value}>{op.label}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-span-4">
                      {rule.field ? renderValue(rule, idx) : (
                        <input
                          type="text"
                          value=""
                          readOnly
                          placeholder="Select a field"
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-gray-50"
                        />
                      )}
                    </div>

                    <div className="col-span-1 flex justify-end">
                      <button
                        type="button"
                        onClick={() => removeRule(idx)}
                        className="rounded-lg px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                        title="Remove"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PmFilterBuilder;
